import React, { useEffect, useState } from 'react';
import CallOut from '../../components/Common/CallOut';
import { useSchool } from '../../DataStore/SchoolContext';
import ActivityLoader from '../../components/Common/ActivityLoader';
import ajaxSchool from '../../util/remote/ajaxSchool';
import Notification from '../../components/Common/Notification';
import { useTheme } from '../../DataStore/ThemeContext';

const FeesGroupConfig = ({ feesGroup }) => {
    const { getClasses, getResidences, classListing, residenceListing } = useSchool();
    const { setSystemFunction } = useTheme();

    const [config, setConfig] = useState({
        gender: [],
        residence: [],
        student_type: [],
        class: [],
    });
    const [loading, setLoading] = useState(false);
    const [info, setInfo] = useState('');

    useEffect(() => {
        getClasses();
        getResidences();
    }, []);

    useEffect(() => {
        if (feesGroup) getConfig();
    }, [feesGroup]);

    useEffect(() => {
        setSystemFunction(() => handleSave);
        return () => setSystemFunction(() => { });
    }, [config]);

    const getConfig = async () => {
        setLoading(true);
        const server_response = await ajaxSchool.fetchFeesGroupConfig(feesGroup ? feesGroup.group_info?.id : null);
        setLoading(false);
        if (server_response.status === "OK") {
            setConfig(server_response.details);
        }
    };

    const handleCheckboxChange = (category, value) => {
        setConfig((prevState) => {
            const isChecked = prevState[category].includes(value);
            return {
                ...prevState,
                [category]: isChecked
                    ? prevState[category].filter((item) => item !== value)
                    : [...prevState[category], value],
            };
        });
    };

    const handleSave = async () => {
        setInfo('');
        setLoading(true);
        const server_response = await ajaxSchool.configureFeesGroup(feesGroup ? feesGroup.group_info?.id : null, config);
        setLoading(false);
        if (server_response.status === "OK") {
            setInfo(<Notification message={server_response.message} type="success" />);
        } else {
            setInfo(<Notification message={server_response.message} type="danger" />);
        }
    };

    // Sub-component for the modern filter cards
    const FilterCard = ({ title, icon, children }) => (
        <div className="col-md-6 mb-4">
            <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                <div className="card-header bg-white border-0 pt-4 px-4 d-flex align-items-center">
                    <div className="rounded-circle d-flex align-items-center justify-content-center mr-3" 
                         style={{ width: '32px', height: '32px', background: '#EEF2FF', color: '#4F46E5' }}>
                        <i className={`fa-solid ${icon} fa-sm`}></i>
                    </div>
                    <h6 className="font-weight-bold mb-0 text-dark" style={{ letterSpacing: '0.02em' }}>{title}</h6>
                </div>
                <div className="card-body px-4 pb-4">
                    <div className="row no-gutters">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );

    const SelectionItem = ({ id, label, isChecked, onChange }) => (
        <div className="col-6 p-1">
            <label className={`d-flex align-items-center p-2 mb-0 transition-all cursor-pointer w-100`}
                style={{
                    borderRadius: '10px',
                    border: isChecked ? '1.5px solid #4F46E5' : '1.5px solid #F1F5F9',
                    background: isChecked ? '#F5F7FF' : '#FFFFFF',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                }}>
                <div className="custom-control custom-checkbox mr-2">
                    <input
                        type="checkbox"
                        className="custom-control-input"
                        id={id}
                        checked={isChecked}
                        onChange={onChange}
                    />
                    <label className="custom-control-label" htmlFor={id}></label>
                </div>
                <span style={{ 
                    fontSize: '13px', 
                    fontWeight: isChecked ? '700' : '500', 
                    color: isChecked ? '#4F46E5' : '#475569' 
                }}>
                    {label}
                </span>
            </label>
        </div>
    );

    return (
        <div className="animate__animated animate__fadeIn">
            <CallOut
                title={`Fees Group: ${feesGroup ? feesGroup.group_info?.group_name : ''}`}
                description="Selected options automatically assign matching students. Unchecked categories are excluded from criteria." 
            />
            
            <div className='mt-4'>{info}</div>
            
            {loading && <div className="text-center py-5"><ActivityLoader /></div>}

            <form className={`row mt-4 ${loading ? 'opacity-50' : ''}`} style={{ pointerEvents: loading ? 'none' : 'auto' }}>
                
                <FilterCard title="Gender" icon="fa-venus-mars">
                    {['Male', 'Female'].map(g => (
                        <SelectionItem 
                            key={g} id={`gender${g}`} label={g} 
                            isChecked={config.gender.includes(g)} 
                            onChange={() => handleCheckboxChange('gender', g)} 
                        />
                    ))}
                </FilterCard>

                <FilterCard title="Student Type" icon="fa-user-graduate">
                    {[
                        { val: 'new', label: 'New Student' },
                        { val: 'continuing', label: 'Continuing' }
                    ].map(type => (
                        <SelectionItem 
                            key={type.val} id={`type${type.val}`} label={type.label} 
                            isChecked={config.student_type.includes(type.val)} 
                            onChange={() => handleCheckboxChange('student_type', type.val)} 
                        />
                    ))}
                </FilterCard>

                <FilterCard title="Residences" icon="fa-house-chimney">
                    {Array.isArray(residenceListing) ? residenceListing.map(item => (
                        <SelectionItem 
                            key={item.id} id={`res${item.id}`} label={item.residence_name} 
                            isChecked={config.residence.includes(item.id)} 
                            onChange={() => handleCheckboxChange('residence', item.id)} 
                        />
                    )) : <div className="col-12"><ActivityLoader /></div>}
                </FilterCard>

                <FilterCard title="Target Classes" icon="fa-school">
                    {Array.isArray(classListing) ? classListing.map(item => (
                        <SelectionItem 
                            key={item.id} id={`cls${item.id}`} label={item.class_name} 
                            isChecked={config.class.includes(item.id)} 
                            onChange={() => handleCheckboxChange('class', item.id)} 
                        />
                    )) : <div className="col-12"><ActivityLoader /></div>}
                </FilterCard>
            </form>
        </div>
    );
};

export default FeesGroupConfig;